import { Location } from 'history';
import React from 'react';
import { parseQuery } from './link_parser';
import { useLocation, useNavigationType } from 'react-router';

// The location type from history doesn't include
// the hash or referer. Referer is needed in a few places,
// TODO - we should remove referer and get it from somewhere else.
export interface IUrl extends Location {
  hash?: string;
  referer?: string;
}

export interface WithUrlProps {
  url: IUrl;
}

export const URLContext = React.createContext<IUrl>({
  pathname: '',
  search: '',
  // A parsed-params object, matching what AppProvider supplies -- consumers
  // index into it, so a string default would silently read as "no params".
  query: {},
  state: '',
  action: 'PUSH',
  key: '',
});

// Minimal slices of IUrl for helpers that only read part of the URL.
// Structurally compatible with IUrl, react-router locations, and the legacy
// v3-style location objects still passed by some callers.
export interface UrlPathQuery {
  pathname?: string;
  query?: Record<string, any>;
}

export interface UrlPathSearch {
  pathname?: string;
  search?: string;
}

export function useURL(): IUrl {
  return React.useContext(URLContext);
}

export type LocationFields = Omit<IUrl, 'referer'>;

export function locationFieldsFrom(
  pathname: string,
  search: string,
  hash: string,
  action: IUrl['action'] = 'POP',
  state: IUrl['state'] = null,
): LocationFields {
  return {
    pathname,
    search,
    hash,
    query: parseQuery(search),
    state,
    action,
    key: 'default',
  };
}

// Overrides AppProvider's URLContext for routed components with a value built
// from react-router's location, so useURL() and the matched route always
// come from the same router state. AppProvider updates its URLContext from
// history events, which fire before react-router commits a navigation.
// Rendered by RootRoute (discovery-ui/src/reverb-js-common/root_route.tsx).
export function RouterURLProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { referer } = useURL();

  const url = React.useMemo<IUrl>(() => ({
    ...locationFieldsFrom(
      location.pathname,
      location.search,
      location.hash,
      navigationType as IUrl['action'],
      location.state,
    ),
    referer,
  }), [location, navigationType, referer]);

  return (
    <URLContext.Provider value={url}>
      {children}
    </URLContext.Provider>
  );
}

export function withUrlContext<TProps>(Wrapped: React.ComponentType<TProps>): React.FunctionComponent<TProps> {
  const Component = (props: TProps) => (
    <URLContext.Consumer>
      {(urlContextObject) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        (<Wrapped url={urlContextObject} {...props} />)
      )}
    </URLContext.Consumer>
  );

  Component.displayName = `withUrlContext(${Wrapped.displayName || Wrapped.name})`;

  return Component;
}
